import NoRecords from '../../shared/NoRecords';
import React, { useMemo, useState } from 'react';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import { fmtCount, fmtPosition, pagePath, fmtSearchTerm } from '../lib/format';

/**
 * Evidence for `nearly_there`.
 *
 * `facts`: { items: [{ query, page, position, impressions, missed_impressions, clicks,
 *                      drill }],
 *            summary: { opportunity_count, total_missed_impressions, position_band } }
 *
 * ── Worklist rows, not bars (V6 §E.3) ───────────────────────────────────────────
 * "Worklist rows: the search, the page it reaches as a path, a position badge, and the
 * missed count right-aligned." V6 reverses the v2 "bars first, drop the URL" rule — the
 * top value (34,263) swamps the next (8,776), so one bar would make nine unreadable
 * slivers, and this panel now sits adjacent to the position ladder and MoverBars, so a
 * fourth bar family is exactly what §4.9 forbids. The page path IS shown in the row now,
 * because the row is the owner's shortlist of searches to look at, and the path tells
 * them which of their pages is the one nearly winning.
 *
 * Rows are rendered in payload order (`missed_impressions` descending); re-sorting to
 * find a "top N" would reorder data the backend already ordered (§8).
 *
 * ── Clicking a row (§E.3) ────────────────────────────────────────────────────────
 * Each row carries its own `drill` object (`gsc.query`, the row's search). A row with a
 * drill is a button that calls `onDrill(row.drill)` — the page runs `fn_drill` and opens
 * the search's pages in the shared `DrillOver`. A row without a `drill` is not drillable
 * (the old captures carry none), so it renders as static text rather than a dead button.
 *
 * `missed_impressions` is labelled "saw you but didn't click" and is NEVER converted to
 * estimated visits, bookings or revenue. No such number exists in this data, and
 * inventing one is the single most tempting piece of arithmetic on the whole page (§8).
 *
 * `summary.position_band` is this card's definition of "just below the top" — rendered
 * so the threshold isn't a mystery the reader has to guess at.
 */

const TOP_N = 10;

/** Export columns — the table's own labels, word for word. See `lib/csv.js`. */
const CSV_COLUMNS = [
  { header: 'Search', get: (r) => fmtSearchTerm(r.query) },
  { header: 'Page', get: (r) => r.page },
  { header: 'Position', get: (r) => r.position },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: "Saw you but didn't click", get: (r) => r.missed_impressions },
];

/** A small position badge — shape + number, so it survives greyscale and print. */
const PositionBadge = ({ position }) => (
  <span className="shrink-0 inline-flex items-center rounded-md bg-amber-500/10 dark:bg-amber-500/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-700 dark:text-amber-300">
    {fmtPosition(position)}
  </span>
);

/**
 * One worklist row: search + page path on the left, a position badge and the missed
 * count on the right. A row with a `drill` is a button (opens the search's pages in
 * `DrillOver`); a row without one is static text.
 */
const WorklistRow = ({ row, onDrill }) => {
  const drillable = Boolean(row?.drill) && typeof onDrill === 'function';
  const inner = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-normal text-gray-900 dark:text-gray-100 break-words">{fmtSearchTerm(row.query)}</span>
        {row.page && (
          <span className="block text-xs text-gray-500 dark:text-gray-400 break-all" title={row.page}>{pagePath(row.page)}</span>
        )}
      </span>
      <PositionBadge position={row.position} />
      <span className="shrink-0 text-right">
        <span className="block tabular-nums text-sm font-medium text-gray-800 dark:text-gray-200">{fmtCount(row.missed_impressions)}</span>
        <span className="block text-xs text-gray-400 dark:text-gray-500">saw, no click</span>
      </span>
      {drillable && (
        <svg className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      )}
    </>
  );
  if (drillable) {
    return (
      <li>
        <button
          type="button"
          data-query={row.query}
          onClick={() => onDrill(row.drill)}
          className="w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset transition-colors"
        >
          {inner}
        </button>
      </li>
    );
  }
  return <li className="flex items-center gap-3 py-2.5 px-2 -mx-2" data-query={row.query}>{inner}</li>;
};

/**
 * Compare two rows on one field for the specialist's sort.
 *
 * Strings compare with `localeCompare` and numbers numerically; a missing value always
 * sorts last regardless of direction, because a blank row rising to the top of a
 * descending sort reads as the largest value rather than as no value.
 */
const compareOn = (key, dir) => (a, b) => {
  const av = a?.[key];
  const bv = b?.[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const result = typeof av === 'string' && typeof bv === 'string'
    ? av.localeCompare(bv)
    : Number(av) - Number(bv);
  return dir === 'asc' ? result : -result;
};

const OpportunityTable = ({ facts, sentence, collapsed = true, onDrill }) => {
  const items = useMemo(() => (Array.isArray(facts?.items) ? facts.items : []), [facts]);
  const summary = facts?.summary || {};

  // §7 expanded: "Full table, sort, position filter, CSV".
  const [sort, setSort] = useState(null);
  const [maxPosition, setMaxPosition] = useState('');

  const onSort = (key) => setSort((prev) => (
    prev?.key === key
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' }
  ));

  const rows = useMemo(() => {
    const limit = Number(maxPosition);
    // An unparseable or empty box is "no filter", not "everything is excluded" — the
    // reader typing a partial number should not watch the table empty out mid-keystroke.
    const filtered = Number.isFinite(limit) && maxPosition !== ''
      ? items.filter((r) => Number(r.position) <= limit)
      : items;
    // No sort selected means payload order, untouched.
    return sort ? [...filtered].sort(compareOn(sort.key, sort.dir)) : filtered;
  }, [items, sort, maxPosition]);

  const topRows = items.slice(0, TOP_N);

  const columns = [
    { key: 'query', label: 'Search', render: (r) => <span className="font-normal text-gray-900 dark:text-gray-100">{fmtSearchTerm(r.query)}</span> },
    {
      key: 'page',
      label: 'Page',
      render: (r) => pagePath(r.page),
      title: (r) => r.page,
    },
    { key: 'position', label: 'Position', align: 'right', render: (r) => fmtPosition(r.position) },
    { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
    {
      key: 'missed_impressions',
      label: "Saw you but didn't click",
      align: 'right',
      render: (r) => fmtCount(r.missed_impressions),
    },
  ];

  return (
    <div className="space-y-3">
      {/* Plain speech, not a glossary entry. This used to read
          `"Just below the top" here means an average position of 5 to 15.` — scare
          quotes around the card's own title, and "here means" is how a manual talks. */}
      {summary.position_band && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          These are searches where you usually land between positions {summary.position_band}.
        </p>
      )}

      {/* The worklist — the owner's shortlist of searches to look at (§E.3). */}
      {topRows.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between gap-3 pb-1 border-b border-gray-100 dark:border-gray-800 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <span>Search &amp; page</span>
            <span>Saw you but didn&apos;t click</span>
          </div>
          <ul data-testid="opportunity-worklist" className="divide-y divide-gray-100 dark:divide-gray-800" aria-label={sentence || "Searches where people saw you but didn't click"}>
            {topRows.map((row, i) => (
              <WorklistRow key={`${row.query}-${i}`} row={row} onDrill={onDrill} />
            ))}
          </ul>
          {/* The count that keeps the list honest about what it is not showing. The
              "select a search" clause is gated on ROWS ACTUALLY CARRYING a drill, not
              merely on `onDrill` being passed — a payload whose rows have no `drill`
              renders static rows, and inviting the reader to "select a search" there
              would be the inverse of a dead affordance. */}
          {summary.opportunity_count != null && (
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Showing {topRows.length} of {fmtCount(summary.opportunity_count)}
              {onDrill && topRows.some((r) => r.drill) ? ' · select a search to see every page it reaches' : ''}
            </p>
          )}
        </div>
      )}

      {topRows.length === 0 && (
        <NoRecords />
      )}

      {/* ── Expanded only: the full table, its controls and the export (§7) ──── */}
      {!collapsed && items.length > 0 && (
        <div className="space-y-3 border-t border-gray-200 dark:border-gray-800 pt-3">
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="gsc-nearly-position" className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Position at most
            </label>
            <input
              id="gsc-nearly-position"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={maxPosition}
              onChange={(e) => setMaxPosition(e.target.value)}
              placeholder="Any"
              className="w-24 text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>

          <PaginatedTable
            columns={columns}
            rows={rows}
            rowKey={(r, i) => `${r.query}-${r.page}-${i}`}
            label="searches"
            ariaLabel={sentence}
            sort={sort}
            onSort={onSort}
          />

          {/* Its own row under the table, left-aligned — the same place every other
              panel puts it, rather than trailing the filter control. */}
          <CsvButton
            panelKey="nearly_there"
            name="Searches where you are just below the top"
            columns={CSV_COLUMNS}
            rows={rows}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * Collapsed is bars with no URLs; expanded is the full table with the page column, the
 * position filter, sortable headers and the CSV. Even a single row gains all of that, so
 * the only case with nothing to reveal is no rows at all.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
OpportunityTable.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;


export default OpportunityTable;
